import * as React from "react"
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/shadcn/ui/accordion"
import { Label } from "@/components/shadcn/ui/label"
import { DynamicGroup as DynamicGroupType } from "@/models/new/forms.types"
import { FormResponse, ResponseValue, parseFieldMediaKey } from "@/models/new/formResponse.types"
import { SelectOption } from "@/types/select-options"
import { ScopedOrganizationEntity } from "@/models/scoped-organization-hierarchy"
import { ConditionValueAliases, shouldShowElement } from "@/utils/conditionalDisplay"
import Field from "../Field"
import Button from "@/components/Button"
import FieldMediaNavigationWrapper from "../FieldMediaNavigationWrapper"
import {
  getDynamicGroupInstances,
  isFieldMediaEligible,
} from "@/components/shared/attachments/attachment-groups"
import type { ReportCreationMedia } from "../report-creation-media"

interface Props {
  field: DynamicGroupType
  formResponse: FormResponse
  onChange: (fieldId: string, value: ResponseValue) => void
  onRemoveInstance?: (field: DynamicGroupType, instance: number) => void
  onInstancesChange?: (instances: number[] | null) => void
  listOptions?: Record<string, SelectOption[]>
  conditionValueAliases?: ConditionValueAliases
  organizations?: ScopedOrganizationEntity[]
  fieldMediaLocators?: Set<string>
  selectedFieldMediaLocator?: string | null
  canCreateFieldMedia?: boolean
  onNavigateToFieldMedia?: (locator: string, options?: { openComposer?: boolean }) => void
  isCreating?: boolean
  creationMedia?: ReportCreationMedia
  onCreationMediaChange?: (media: ReportCreationMedia) => void
}

const DynamicGroup = (props: Props) => {
  const { field, formResponse, onChange } = props
  const [openItems, setOpenItems] = React.useState<string[]>([])
  const [createdInstances, setCreatedInstances] = React.useState<number[]>([])

  // Use the same persisted and pending row sources as the editor's reindexers.
  const instances = React.useMemo(() => {
    const instanceSet = new Set(
      getDynamicGroupInstances(formResponse.responses, field.id, formResponse.fieldMedia, [
        ...Object.keys(props.creationMedia?.responseUploads ?? {}),
        ...Object.keys(props.creationMedia?.fieldMedia ?? {}),
      ]),
    )

    createdInstances.forEach((instance) => instanceSet.add(instance))
    return Array.from(instanceSet).sort((a, b) => a - b)
  }, [
    createdInstances,
    formResponse.responses,
    formResponse.fieldMedia,
    props.creationMedia,
    field.id,
  ])

  // Lift the accordion's resolved response/media/local instance set to the editor so
  // compaction and server-error row numbers use the same source as accordion labels.
  // Report up only when `instances` actually changes (memo ref
  // changes), via a ref so the parent's callback identity does not retrigger this.
  const onInstancesChangeRef = React.useRef(props.onInstancesChange)
  onInstancesChangeRef.current = props.onInstancesChange
  React.useEffect(() => {
    onInstancesChangeRef.current?.(instances)
  }, [instances])

  // On unmount (e.g. a `conditionalDisplay` on this group hides it), tell the editor to
  // drop our lifted snapshot. `null` means "unmounted", not "empty": the editor deletes
  // the `groupId` so `findQuestion`'s `?? getDynamicGroupInstances(responses, ...)`
  // fallback reclaims responses-only numbering, which matches the accordion after a
  // re-show (ephemeral `createdInstances` reset). Storing `[]` would shadow that
  // fallback (`??` does not fire on `[]`) and yield a numberless toast.
  React.useEffect(() => {
    return () => {
      onInstancesChangeRef.current?.(null)
    }
  }, [])

  const handleAddInstance = () => {
    const nextInstance = instances.length > 0 ? Math.max(...instances) + 1 : 1
    setCreatedInstances((current) => [...current, nextInstance])
    setOpenItems((current) => [...current, `instance-${nextInstance}`])

    // Initialize fields only when their response schema has a valid empty value.
    // Date and geolocation responses are absent until the user supplies a real value.
    field.fields.forEach((groupField) => {
      const key = `${field.id}.${nextInstance}.${groupField.name}`

      let emptyFieldValue: unknown = ""
      if (groupField.type === "select") {
        emptyFieldValue = groupField.selectionType === "multi" ? [] : ""
      } else if (groupField.type === "file" || groupField.type === "signature") {
        emptyFieldValue = []
      } else if (groupField.type === "datepicker" || groupField.type === "geolocation") {
        return
      }

      const emptyValue: ResponseValue = {
        fieldName: groupField.name,
        type: groupField.type,
        groupId: field.id,
        instance: nextInstance,
        value: emptyFieldValue,
        ...(groupField.type === "select" ? { selectionType: groupField.selectionType } : {}),
      } as ResponseValue

      onChange(key, emptyValue)
    })
  }

  const handleRemoveInstance = (instanceToRemove: number) => {
    // Compact survivors to the same contiguous 1..N scheme the persisted reindexers
    // (editor's responses/fieldMedia/creationMedia) use, so the rendered row locators
    // (`${field.id}.${instance}.${groupField.name}`) agree with the compacted media
    // keys. The remap is derived from the full response/media/local `instances`
    // set, not just `createdInstances`, so it stays correct when some
    // rows are response-backed and some are ghosts (e.g. a geolocation-only group
    // mixed with text-bearing rows).
    const survivors = instances.filter((instance) => instance !== instanceToRemove)
    const remap = new Map<number, number>()
    survivors.forEach((oldInstance, index) => remap.set(oldInstance, index + 1))
    setCreatedInstances((current) =>
      current
        .filter((instance) => instance !== instanceToRemove)
        .map((instance) => remap.get(instance) ?? instance),
    )
    setOpenItems((current) =>
      survivors.flatMap((instance) =>
        current.includes(`instance-${instance}`) ? [`instance-${remap.get(instance)}`] : [],
      ),
    )
    props.onRemoveInstance?.(field, instanceToRemove)
  }

  React.useEffect(() => {
    const escapedId = field.id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    const pattern = new RegExp(`^${escapedId}\\.(\\d+)\\.`)
    const backedInstances = new Set<number>()
    Object.entries(formResponse.responses).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      const match = key.match(pattern)
      if (match) backedInstances.add(parseInt(match[1], 10))
    })

    setCreatedInstances((current) => {
      if (current.length === 0) return current
      const next = current.filter((instance) => !backedInstances.has(instance))
      return next.length === current.length ? current : next
    })
  }, [field.id, formResponse.responses])

  React.useEffect(() => {
    if (!props.selectedFieldMediaLocator) {
      return
    }

    const parsedKey = parseFieldMediaKey(props.selectedFieldMediaLocator)
    if (!("groupId" in parsedKey) || parsedKey.groupId !== field.id) {
      return
    }

    const nextItem = `instance-${parsedKey.instance}`
    setOpenItems((currentItems) =>
      currentItems.includes(nextItem) ? currentItems : [...currentItems, nextItem],
    )
  }, [field.id, props.selectedFieldMediaLocator])

  return (
    <div className="w-full min-w-0 p-4">
      <div className="flex justify-between items-center mb-2">
        <Label className="mb-0">{field.label}</Label>
        <Button label="+ Add" onClick={handleAddInstance} type="secondary" />
      </div>

      {instances.length === 0 ? (
        <div className="text-gray-500 italic text-sm">
          No entries yet. Click "Add" to create one.
        </div>
      ) : (
        <Accordion multiple value={openItems} onValueChange={setOpenItems}>
          {instances.map((instance, index) => (
            <AccordionItem
              key={instance}
              value={`instance-${instance}`}
              className="border border-[#E2E8F0] rounded-[5px] mb-2"
            >
              <AccordionTrigger className="hover:no-underline px-4">
                <div className="flex flex-row items-center justify-between w-full">
                  <h4 className="text-sm font-medium">
                    {field.label} #{index + 1}
                  </h4>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleRemoveInstance(instance)
                      }}
                      className="px-2 text-sm text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent className="p-4">
                {/* Size evidence wrappers as cells; fields must fill, not halve, each cell. */}
                <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,22rem),1fr))] [&>div]:min-w-0 [&>div>div:first-child]:w-full">
                  {field.fields
                    .filter((groupField) =>
                      shouldShowElement(
                        groupField.conditionalDisplay,
                        formResponse,
                        {
                          groupId: field.id,
                          instance,
                          fields: field.fields,
                        },
                        props.conditionValueAliases,
                      ),
                    )
                    .map((groupField) => {
                      const key = `${field.id}.${instance}.${groupField.name}`
                      const pendingFieldMedia = props.creationMedia?.fieldMedia[key] || {
                        target: {
                          fieldId: groupField.id,
                          fieldName: groupField.name,
                          fieldLabel: groupField.label,
                          groupId: field.id,
                          instance,
                        },
                        uploads: [],
                      }
                      return (
                        <FieldMediaNavigationWrapper
                          key={key}
                          locator={key}
                          hasFieldMedia={Boolean(props.fieldMediaLocators?.has(key))}
                          canCreateFieldMedia={Boolean(
                            props.canCreateFieldMedia && isFieldMediaEligible(groupField),
                          )}
                          isSelected={props.selectedFieldMediaLocator === key}
                          onNavigateToFieldMedia={props.onNavigateToFieldMedia}
                          pendingFieldMedia={pendingFieldMedia}
                          onPendingFieldMediaChange={
                            props.creationMedia &&
                            props.onCreationMediaChange &&
                            isFieldMediaEligible(groupField)
                              ? (bundle) => {
                                  const fieldMedia = { ...props.creationMedia!.fieldMedia }
                                  if (bundle) fieldMedia[key] = bundle
                                  else delete fieldMedia[key]
                                  props.onCreationMediaChange!({
                                    ...props.creationMedia!,
                                    fieldMedia,
                                  })
                                }
                              : undefined
                          }
                        >
                          <Field
                            field={groupField}
                            value={formResponse.responses[key]}
                            onChange={(_, value) => {
                              const updatedValue = {
                                ...value,
                                groupId: field.id,
                                instance: instance,
                              }
                              onChange(key, updatedValue)
                            }}
                            listOptions={props.listOptions}
                            organizations={props.organizations}
                            formResponse={formResponse}
                            canCreateFieldMedia={props.canCreateFieldMedia}
                            isCreating={props.isCreating}
                            pendingUploads={props.creationMedia?.responseUploads[key]}
                            onPendingUploadsChange={
                              props.creationMedia && props.onCreationMediaChange
                                ? (uploads) => {
                                    const responseUploads = {
                                      ...props.creationMedia!.responseUploads,
                                    }
                                    if (uploads.length > 0) responseUploads[key] = uploads
                                    else delete responseUploads[key]
                                    props.onCreationMediaChange!({
                                      ...props.creationMedia!,
                                      responseUploads,
                                    })
                                  }
                                : undefined
                            }
                          />
                        </FieldMediaNavigationWrapper>
                      )
                    })}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  )
}

export default DynamicGroup
